import { Controller } from "@hotwired/stimulus"

// Expand/collapse a rail. Born on docs (where the controller sits on
// .assistant-shell, an ancestor of both the rail and its toggle button) and
// reused verbatim by the persona list pane on /apps/persona: it flips its
// state class(es) on its own element and persists the choice so it survives
// navigation.
//
// Two modes, chosen by the `hideable` value:
//   * Two-state (default, persona): shown <-> expanded. Persists "true"/"false"
//     under storageKey, byte-identical to the original controller so existing
//     persona preferences keep working.
//   * Three-state (docs, hideable: true): shown -> expanded -> hidden -> shown.
//     "hidden" fully collapses the rail so the content column takes the space.
//     Persists the state name; the toggle icon depicts the CURRENT state while
//     the aria-label/title announce the action that reaches the NEXT one.
//   * Two-state simple (simple: true, a per-user preference from
//     Settings -> Navigation): expanded <-> hidden. Same icons, labels and
//     persistence as three-state — it only drops the icon-only stop, for people
//     who want one click between "nav is there" and "nav is gone". A "shown"
//     value left in storage by the three-state cycle resolves to "expanded",
//     so switching modes never lands the rail in a state its cycle can't reach.
//
// Everything is parameterised so a second rail needs no second controller:
// storageKey (which preference), the expanded/hidden classes, defaultExpanded
// (docs starts collapsed; the persona pane starts open), the per-state labels
// and — in hideable mode — the per-state toggle icons.
export default class extends Controller {
    static targets = ["toggle"]
    static classes = ["expanded", "hidden"]
    static values = {
        storageKey: { type: String, default: "docs_rail_expanded" },
        defaultExpanded: { type: Boolean, default: false },
        hideable: { type: Boolean, default: false },
        simple: { type: Boolean, default: false },
        expandLabel: { type: String, default: "Expand navigation" },
        collapseLabel: { type: String, default: "Collapse navigation" },
        hideLabel: { type: String, default: "Hide navigation" },
        showLabel: { type: String, default: "Show navigation" },
        // One glyph per CURRENT state: narrow sidebar (icon rail), sidebar with
        // text rows (expanded), and the hamburger when the rail is hidden —
        // the universal "navigation lives here" mark. (An empty bi-square
        // frame was tried for hidden and read as an unchecked checkbox.)
        shownIcon: { type: String, default: "bi-layout-sidebar" },
        expandedIcon: { type: String, default: "bi-layout-text-sidebar-reverse" },
        hiddenIcon: { type: String, default: "bi-list" }
    }

    connect() {
        // Render without persisting. Storing the state on connect meant the
        // FIRST page load wrote a preference nobody had expressed - so an
        // account-level default (nav_rail_expanded) could never reach anybody
        // who had loaded a page once, which is everybody. localStorage should
        // record a choice, not a render.
        this.mobileMedia = window.matchMedia("(max-width: 767.98px)")
        this.onViewportChange = () => {
            this.closeMobile()
            this._syncToggle(this._current())
        }
        this.onMobileKey = event => this._mobileKey(event)
        // A drawer control that opens something over the page (Search) closes
        // the drawer first, so the two overlays never stack. Runs before the
        // action itself: this element sits below <body>, where search lives.
        this.onMobileDismiss = event => {
            if (this.mobileOpen && event.target.closest("[data-mobile-nav-dismiss]")) this.closeMobile({ restoreFocus: false })
        }
        this.element.addEventListener("click", this.onMobileDismiss)
        this.beforeCache = () => this.closeMobile({ restoreFocus: false })
        this.mobileMedia.addEventListener("change", this.onViewportChange)
        document.addEventListener("keydown", this.onMobileKey)
        document.addEventListener("turbo:before-cache", this.beforeCache)
        this._apply(this._stored(), { persist: false })
    }

    disconnect() {
        this.closeMobile({ restoreFocus: false })
        this.mobileMedia?.removeEventListener("change", this.onViewportChange)
        document.removeEventListener("keydown", this.onMobileKey)
        this.element.removeEventListener("click", this.onMobileDismiss)
        document.removeEventListener("turbo:before-cache", this.beforeCache)
    }

    toggle() {
        if (this.mobileMedia.matches && this.element.querySelector(".assistant-bottom-nav")) {
            if (this.mobileOpen) this.closeMobile()
            else this._openMobile()
            return
        }
        const states = this._states()
        const next = states[(states.indexOf(this._current()) + 1) % states.length]
        this._apply(next)
    }

    _openMobile() {
        this.mobileNav = this.element.querySelector(".assistant-bottom-nav")
        this.returnFocus = document.activeElement
        this.mobileOpen = true
        this.element.classList.add("is-mobile-nav-open")
        this.mobileNav.setAttribute("role", "dialog")
        this.mobileNav.setAttribute("aria-modal", "true")
        this.mobileNav.setAttribute("aria-label", "Navigation")
        this.inerted = [...this.element.querySelectorAll(".assistant-main, .assistant-phone > aside")]
            .filter(element => !element.inert)
        this.inerted.forEach(element => { element.inert = true })
        this._syncToggle(this._current())
        this.mobileNav.querySelector(".mobile-nav-close").focus()
    }

    closeMobile({ restoreFocus = true } = {}) {
        if (!this.mobileOpen) return
        this.mobileOpen = false
        this.element.classList.remove("is-mobile-nav-open")
        ;["role", "aria-modal", "aria-label"].forEach(name => this.mobileNav.removeAttribute(name))
        this.inerted.forEach(element => { element.inert = false })
        this._syncToggle(this._current())
        if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus()
    }

    _mobileKey(event) {
        if (!this.mobileOpen || event.defaultPrevented) return
        if (event.key === "Escape") {
            // Give an open child menu the first Escape.
            if (this.mobileNav.querySelector(".rail-chat-menu:not([hidden])") || document.querySelector(".is-open[role='menu'], [popover]:popover-open")) return
            event.preventDefault()
            this.closeMobile()
        } else if (event.key === "Tab") {
            const controls = [...this.mobileNav.querySelectorAll("a[href], button, input, summary, [tabindex='0']")]
                .filter(element => !element.disabled && element.getClientRects().length && !element.closest("[hidden]"))
            const first = controls[0], last = controls[controls.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }
    }

    // Two-state rails skip "hidden" entirely, so persona keeps its exact cycle.
    // The simple mode skips the icon-only "shown" stop instead.
    _states() {
        if (this.simpleValue) return ["expanded", "hidden"]
        return this.hideableValue ? ["shown", "expanded", "hidden"] : ["shown", "expanded"]
    }

    // Whether this rail owns a "hidden" state at all — the simple mode is built
    // on the same hidden class as the three-state cycle.
    _canHide() {
        return this.hideableValue || this.simpleValue
    }

    _current() {
        if (this._canHide() && this.element.classList.contains(this._hiddenClass())) return "hidden"
        if (this.element.classList.contains(this._expandedClass())) return "expanded"
        // Simple mode has no icon-only stop, so anything that isn't hidden is
        // expanded — including the very first paint before a class is set.
        return this.simpleValue ? "expanded" : "shown"
    }

    _apply(state, { persist = true } = {}) {
        // A desktop dock has no rail width, even when a left-rail preference
        // is stored. Its mobile drawer still uses the same controls and chats.
        const dock = this.element.matches(".assistant-shell") && document.body.classList.contains("jane-nav-bottom")
        this.element.classList.toggle(this._expandedClass(), !dock && state === "expanded")
        if (this._canHide()) {
            this.element.classList.toggle(this._hiddenClass(), !dock && state === "hidden")
        }
        if (persist) this._persist(state)
        this._syncToggle(state)
    }

    // The label announces the action that reaches the NEXT state, matching the
    // original two-state behaviour (collapsed shows "Expand", expanded shows
    // "Collapse"); three-state adds hide -> show. The icon instead depicts the
    // state the rail is IN right now, so it always matches what's on screen.
    _syncToggle(state) {
        if (!this.hasToggleTarget) return

        let label
        let icon
        let expanded
        if (this._canHide()) {
            if (state === "expanded") {
                label = this.hideLabelValue
                icon = this.expandedIconValue
                expanded = true
            } else if (state === "hidden") {
                label = this.showLabelValue
                icon = this.hiddenIconValue
                expanded = false
            } else {
                label = this.expandLabelValue
                icon = this.shownIconValue
                expanded = false
            }
        } else {
            expanded = state === "expanded"
            label = expanded ? this.collapseLabelValue : this.expandLabelValue
        }

        if (this.mobileMedia?.matches && this.element.querySelector(".assistant-bottom-nav")) {
            label = this.mobileOpen ? "Close navigation" : "Open navigation"
            icon = "bi-list"
            expanded = !!this.mobileOpen
        }
        this.toggleTarget.setAttribute("aria-label", label)
        this.toggleTarget.setAttribute("title", label)
        this.toggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")

        // Only the hideable (docs) toggle owns its icon; the two-state rails keep
        // whatever icon their markup shipped with.
        if (icon) {
            const glyph = this.toggleTarget.querySelector("i")
            if (glyph) glyph.className = `bi ${icon}`
        }
    }

    _expandedClass() {
        return this.hasExpandedClass ? this.expandedClass : "is-rail-expanded"
    }

    _hiddenClass() {
        return this.hasHiddenClass ? this.hiddenClass : "is-rail-hidden"
    }

    // Nothing stored yet means "never chosen", which is this rail's own default —
    // not "collapsed". Docs leaves defaultExpanded false, so it is unchanged.
    // Legacy values are booleans ("true"/"false"); map them onto state names so
    // an existing preference survives the two-state -> three-state upgrade.
    _stored() {
        let raw = null
        try {
            raw = window.localStorage.getItem(this.storageKeyValue)
        } catch {
            raw = null
        }
        // Simple mode only distinguishes hidden from expanded, so every other
        // stored value (including a three-state "shown") resolves to expanded.
        if (this.simpleValue) return raw === "hidden" ? "hidden" : "expanded"
        if (raw === null) return this.defaultExpandedValue ? "expanded" : "shown"
        // Shared product rails must restore the same width when a destination
        // uses a two-state toggle (Persona) instead of the three-state cycle.
        if (["shown", "expanded"].includes(raw)) return raw
        if (this.hideableValue && raw === "hidden") return raw
        return raw === "true" ? "expanded" : "shown"
    }

    // Two-state rails keep the original boolean encoding; only hideable rails
    // persist state names (they need to tell "hidden" from "shown").
    _persist(state) {
        const raw = this._canHide() ? state : (state === "expanded" ? "true" : "false")
        try {
            window.localStorage.setItem(this.storageKeyValue, raw)
        } catch {
            // localStorage unavailable — the toggle still works for this view
        }
    }
}
